import { DEFAULT_USER_PREFS, formatDateTimePref } from "@strat/api";
import type { UserPrefs } from "@strat/api";

/**
 * Dates and times follow the reader's choice (Settings → Display): dd/mm/yyyy
 * with a 24-hour clock in the local timezone unless they picked otherwise.
 * Built explicitly rather than via toLocaleString so the format is identical
 * whichever locale the browser happens to be set to.
 *
 * The choice is held here, set by UserPrefsProvider, rather than passed to
 * every call: these formatters run in hundreds of table cells that should not
 * each have to ask for it.
 */
function pad(value: number): string {
  return String(value).padStart(2, "0");
}

let dateDisplay: Pick<UserPrefs["display"], "dateFormat" | "timeZone"> = DEFAULT_USER_PREFS.display;

/** Called by UserPrefsProvider whenever the reader's date choices change. */
export function setDateDisplay(display: Pick<UserPrefs["display"], "dateFormat" | "timeZone">): void {
  dateDisplay = { dateFormat: display.dateFormat, timeZone: display.timeZone };
}

export function currentDateDisplay(): Pick<UserPrefs["display"], "dateFormat" | "timeZone"> {
  return dateDisplay;
}

/** 13/09/2026 14:30 */
export function formatDateTime(ms: number): string {
  return formatDateTimePref(ms, dateDisplay);
}

/** 13/09/2026 */
export function formatDate(ms: number): string {
  return formatDateTimePref(ms, dateDisplay, { time: false });
}

/** 13/09/26 — for dense tables */
export function formatDateCompact(ms: number): string {
  return formatDateTimePref(ms, dateDisplay, { time: false, compact: true });
}

/** 14:30:05 */
export function formatTime(ms: number): string {
  const d = new Date(ms);
  return dateDisplay.timeZone === "utc"
    ? `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    : `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(new Date(iso).getTime());
}

/** ISO yyyy-mm-dd — required by <input type="date">, never shown raw to the user. */
export function toDateInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Numbers stay in the international trading convention (1,234.56) to match the
 * price axis and the wider crypto tooling ecosystem.
 */
export function formatPrice(value: number): string {
  const digits = Math.abs(value) >= 1000 ? 2 : Math.abs(value) >= 1 ? 4 : 8;
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: digits });
}

export function formatPct(value: number, digits = 2): string {
  return `${value.toFixed(digits)}%`;
}

export function formatSignedPct(value: number, digits = 2): string {
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

export function formatMoney(value: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCompact(value: number): string {
  return Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value);
}

/**
 * A trade's return as a percent.
 *
 * `Trade.pnlPct` is a **fraction** on the wire — 0.012 is +1.2%. The two
 * prototypes disagreed about this, one storing a percent and the other a
 * fraction under the same field name, so the conversion happens here and
 * nowhere else.
 */
export function tradePct(trade: { pnlPct: number }): number {
  return trade.pnlPct * 100;
}

/**
 * A count with its noun, agreeing in number: `count(1, "market")` → "1 market".
 *
 * Every one of these strings was written while looking at test data that had
 * more than one of whatever it was counting, so "Tested on 1 markets" only
 * showed up the first time somebody ran a robustness check against a single
 * symbol — which is the most likely way to run the first one.
 *
 * Irregular plurals are passed explicitly rather than guessed at.
 */
export function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? singular : plural}`;
}

/** 1,024-based, because that is what a memory or disk figure elsewhere on the machine will say. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** The two largest units that matter: "3d 4h", "12m 5s", "40s". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const parts: [number, string][] = [
    [Math.floor(s / 86_400), "d"],
    [Math.floor((s % 86_400) / 3600), "h"],
    [Math.floor((s % 3600) / 60), "m"],
    [s % 60, "s"],
  ];
  const first = parts.findIndex(([n]) => n > 0);
  if (first < 0) return "0s";
  return parts
    .slice(first, first + 2)
    .filter(([n], i) => i === 0 || n > 0)
    .map(([n, u]) => `${n}${u}`)
    .join(" ");
}
